import {
  useCallback,
  useRef,
  useState,
  Dispatch,
  SetStateAction,
  MutableRefObject,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import t from 'react-translate';

import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import {
  startRolePlaySession,
  StartSessionPayload,
  setAiRolePlaySubmissionClosingReason,
  startAvatarSession,
  stopAvatarSession,
  AvatarSessionPayload,
  AvatarSessionError,
} from 'redux/actions/ai-role-play';
import { wrapThunkAction } from 'redux/utils';
import { danger } from 'styles/global_defaults/colors';
import { RootState } from 'redux/schemas';
import { RolePlayInteractionMode } from 'redux/schemas/models/ai-role-play';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';

import { MediaSourceType, requestMediaStream, stopMediaStreams } from 'recording/services/media-stream';
import {
  AIRolePlayStatus,
  COUNTDOWN_DURATION_MS,
  MAX_INACTIVITY_COUNT,
  SessionData,
  VoiceCardState,
  VoiceRecordingState,
} from '../ai-conversation-modal/context';
import { ClosingReason, retryTtsStream } from '../ai-conversation-modal/stream-ai-response';
import connectVoiceStream, {
  VoiceStreamConnection,
  VoiceStreamHandlers,
  isFatalCloseCode,
  decodeRolePlayIdFromToken,
} from '../ai-conversation-modal/voice-stream-socket';
import useTokenRefresh from './use-token-refresh';
import useVoiceRecorder from './use-voice-recorder';
import createAudioPlaybackPipeline, { AudioPlaybackPipeline } from './create-audio-playback-pipeline';
import createAvatarVideoPipeline, { AvatarConnectionState, AvatarVideoPipeline } from './create-avatar-video-pipeline';
import useVideoTurnRecorder, { VideoTurn } from './use-video-turn-recorder';
import useVideoTurnUploader from './use-video-turn-uploader';

// AbortController cancellations surface as a DOMException/Error with name='AbortError';
// the message varies across browsers, so always check .name.
const isAbortError = (err: unknown): boolean => (err as Error)?.name === 'AbortError';

// The 409 lock-conflict retry on avatar-session minting waits this long when the
// server doesn't provide its own retry_after.
const AVATAR_SESSION_DEFAULT_RETRY_AFTER_S = 2;

// Video mode plays no browser-side audio — the avatar's LiveKit audio track carries
// the AI's speech — so the turn loop runs against an inert pipeline. Its playbackDone
// must NOT resolve immediately: the turn loop awaits it before checking closingReason
// and (in the finally block) tearing down the avatar session, so an eagerly-resolved
// promise would stop the session while the avatar is still mid-utterance. Instead it
// resolves only once the caller signals the avatar has actually finished this turn's
// speech (onAvatarSpeechEnded) or degraded to text-only (onAvatarError) — see
// resolveAvatarPlaybackDone below. Card SPEAKING/IDLE states come from the same events.
const createNoopPlaybackPipeline = (playbackDone: Promise<void>): AudioPlaybackPipeline => ({
  appendChunk: () => {},
  finishStreaming: () => {},
  discardSentence: () => {},
  playbackDone,
  cleanup: () => {},
});

interface UseVoiceAiConversationHandlersParams {
  audioStreamRef: MutableRefObject<MediaStream | null>
  aiRolePlayId: number;
  setAiRolePlayStatus: Dispatch<SetStateAction<AIRolePlayStatus>>;
  setShowScenarioDetails: Dispatch<SetStateAction<boolean>>;
  sessionData: SessionData;
  setSessionData: Dispatch<SetStateAction<SessionData>>;
  setIsTimeout: Dispatch<SetStateAction<boolean>>;
  abortControllerRef: MutableRefObject<AbortController | null>;
  setIsRolePlayStarting: Dispatch<SetStateAction<boolean>>;
  setMicrophoneEnabled: Dispatch<SetStateAction<boolean>>;
  setOpeningNarrative: Dispatch<SetStateAction<string>>;
  setVoiceRecordingState: Dispatch<SetStateAction<VoiceRecordingState | null>>;
  resetConversation: () => void;
  setInactivityCount: Dispatch<SetStateAction<number>>;
  inactivityCount: number;
  /**
   * Voice and video role plays share this hook's turn loop. VIDEO steers it to:
   * camera+mic capture, an avatar session + LiveKit room instead of browser-side
   * audio playback, per-turn recording/upload of both the learner's camera and
   * the avatar's video, and 4011 recovery. Defaults to VOICE.
   */
  mode?: RolePlayInteractionMode;
}

interface UseVoiceAiConversationHandlersReturn {
  handleStartVoiceConversation: () => void;
  aiVoiceCardState: VoiceCardState;
  learnerVoiceCardState: VoiceCardState;
  aiMediaStream: MediaStream | null;
  learnerMediaStream: MediaStream | null;
  isStreamError: boolean;
  handleRestartFromStreamError: () => void;
  /** Video mode: binds the AI card's <video> element to the LiveKit pipeline. */
  attachAvatarVideo: (videoEl: HTMLVideoElement | null) => void;
  /** Video mode: LiveKit room connection state, null while no avatar session is live. */
  avatarConnectionState: AvatarConnectionState | null;
  /** Video mode: background image URL chroma-keyed in behind the avatar, null for the avatar's native background. */
  avatarBackgroundImageUrl: string | null;
}

const useVoiceAiConversationHandlers = ({
  setAiRolePlayStatus,
  setShowScenarioDetails,
  sessionData,
  setSessionData,
  setIsTimeout,
  abortControllerRef,
  setIsRolePlayStarting,
  setMicrophoneEnabled,
  audioStreamRef,
  aiRolePlayId,
  setOpeningNarrative,
  setVoiceRecordingState,
  resetConversation,
  setInactivityCount,
  inactivityCount,
  mode = RolePlayInteractionMode.VOICE,
}: UseVoiceAiConversationHandlersParams): UseVoiceAiConversationHandlersReturn => {
  const dispatch = useDispatch();
  const currentCatalogId = useSelector((state: RootState) => state.app.currentCatalogId);

  const isVideoMode = mode === RolePlayInteractionMode.VIDEO;

  const [aiVoiceCardState, setAiVoiceCardState] = useState(VoiceCardState.IDLE);
  const [learnerVoiceCardState, setLearnerVoiceCardState] = useState(VoiceCardState.IDLE);
  const [aiMediaStream, setAiMediaStream] = useState<MediaStream | null>(null);
  const [learnerMediaStream, setLearnerMediaStream] = useState<MediaStream | null>(null);
  const [isStreamError, setIsStreamError] = useState(false);
  const [avatarConnectionState, setAvatarConnectionState] = useState<AvatarConnectionState | null>(null);
  const [avatarBackgroundImageUrl, setAvatarBackgroundImageUrl] = useState<string | null>(null);

  // Video mode: LiveKit pipeline for the whole conversation session, plus the AI
  // card's <video> element so tracks can attach whether the element or the
  // pipeline shows up first.
  const avatarPipelineRef = useRef<AvatarVideoPipeline | null>(null);
  const avatarVideoElRef = useRef<HTMLVideoElement | null>(null);
  // Video mode: the just-recorded learner turn, held until turn_complete delivers
  // the turn_id that correlates it with the submission's video turn on the server.
  const pendingLearnerVideoTurnRef = useRef<VideoTurn | null>(null);
  // Video mode: the avatar segment's stop-promise for the current turn — set on
  // avatar_speech_ended (the recorder finalizes its blob asynchronously) and
  // consumed alongside the learner blob when turn_complete lands.
  const pendingAvatarVideoTurnRef = useRef<Promise<VideoTurn | null> | null>(null);

  // Holds the resolve function of the restart-wait Promise; called when user clicks Restart
  const restartResolverRef = useRef<(() => void) | null>(null);
  const hasReceivedNarrativeRef = useRef(false);
  // Monotonically increasing counter — each handleStartVoiceConversation invocation captures
  // its own value so the finally block can detect whether a newer session has taken over.
  const sessionIdRef = useRef(0);

  // Session data ref avoids stale closures across async cycles
  const sessionDataRef = useRef<SessionData>(sessionData);
  sessionDataRef.current = sessionData;

  const submissionIdRef = useRef<number | null>(sessionData.submissionId);
  submissionIdRef.current = sessionData.submissionId;

  const inactivityCountRef = useRef(inactivityCount);
  inactivityCountRef.current = inactivityCount;

  const {
    recordLearnerTurn,
    setMicActive,
    initializeAnalyser,
    cleanupRecorder,
    notifyTranscriptFinal,
  } = useVoiceRecorder({ audioStreamRef, setInactivityCount });

  const { ensureFreshToken } = useTokenRefresh({ sessionData, setSessionData });

  const {
    startTurn: startLearnerVideoTurn,
    stopTurn: stopLearnerVideoTurn,
    cancelTurn: cancelLearnerVideoTurn,
  } = useVideoTurnRecorder();
  const {
    startTurn: startAvatarVideoTurn,
    stopTurn: stopAvatarVideoTurn,
    cancelTurn: cancelAvatarVideoTurn,
  } = useVideoTurnRecorder();
  const { enqueueTurnUpload } = useVideoTurnUploader();

  const attachAvatarVideo = useCallback((videoEl: HTMLVideoElement | null) => {
    avatarVideoElRef.current = videoEl;
    avatarPipelineRef.current?.attach(videoEl);
  }, []);

  // Video mode: discards any in-flight avatar recording plus the held blob —
  // used on error/retry/teardown paths so a recorder never leaks across turns
  // or sessions and a stale segment can't upload against a later turn.
  const discardAvatarVideoTurn = useCallback(() => {
    cancelAvatarVideoTurn();
    pendingAvatarVideoTurnRef.current = null;
  }, [cancelAvatarVideoTurn]);

  // Pairs the held learner and avatar blobs with the turn_id from turn_complete
  // and hands them to the background upload queue with their roles. The opening
  // turn has no learner recording (avatar segment only), and degraded turns
  // (avatar_error) have no avatar segment.
  const queuePendingVideoTurnUploads = useCallback((turnId?: string) => {
    const learnerTurn = pendingLearnerVideoTurnRef.current;
    pendingLearnerVideoTurnRef.current = null;
    // turn_complete can outrun avatar_speech_ended — stop the recorder now so
    // the segment still covers everything the avatar said this turn. A no-op
    // (resolving null) when nothing is recording.
    const avatarTurnPromise = pendingAvatarVideoTurnRef.current ?? stopAvatarVideoTurn();
    pendingAvatarVideoTurnRef.current = null;

    if (!turnId) {
      // Without a turn_id the blobs can't be attached server-side — drop them so
      // they can't mis-correlate with a later turn.
      // eslint-disable-next-line no-console
      console.warn('turn_complete arrived without a turn_id — dropping this turn\'s video segments');
      return;
    }

    const rolePlayIdClaim = decodeRolePlayIdFromToken(sessionDataRef.current.jwtToken ?? '');
    if (!rolePlayIdClaim || !submissionIdRef.current) return;

    const uploadContext = {
      turnId,
      rolePlayId: rolePlayIdClaim,
      submissionId: submissionIdRef.current,
      catalogId: currentCatalogId,
    };

    if (learnerTurn) {
      enqueueTurnUpload({
        ...uploadContext,
        role: 'learner',
        blob: learnerTurn.blob,
        durationSeconds: learnerTurn.durationSeconds,
      });
    }
    avatarTurnPromise.then((avatarTurn) => {
      if (!avatarTurn) return;
      enqueueTurnUpload({
        ...uploadContext,
        role: 'ai',
        blob: avatarTurn.blob,
        durationSeconds: avatarTurn.durationSeconds,
      });
    });
  }, [enqueueTurnUpload, currentCatalogId, stopAvatarVideoTurn]);

  // Mutable so each conversational turn can swap in its own callbacks without tearing
  // down and reopening the socket — the connection itself lives for the whole session.
  const handlersRef = useRef<VoiceStreamHandlers>({
    onChunk: () => {},
    onAudioDelta: () => {},
    onAudioDone: () => {},
    onAudioError: () => {},
    onError: () => {},
    onTranscriptError: () => {},
    onTurnComplete: () => {},
  });

  // Ref allows onConfirm (inside the callback) to call handleStartVoiceConversation
  // without creating a circular useCallback dependency.
  const handleStartVoiceConversationRef = useRef<() => void>(() => {});

  const handleStartVoiceConversation = useCallback(async () => {
    // Abort any in-flight session so its while loop exits before this one starts.
    // Without this, Start Over leaves the old session running concurrently.
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Capture this session's ID before any await so the finally block can detect
    // whether a newer session has taken over (immune to audioStreamRef timing gaps).
    sessionIdRef.current += 1;
    const thisSessionId = sessionIdRef.current;

    hasReceivedNarrativeRef.current = false;
    setInactivityCount(0);
    setOpeningNarrative('');
    setAiRolePlayStatus(AIRolePlayStatus.IN_PROGRESS);
    setIsRolePlayStarting(true);
    setShowScenarioDetails(false);
    // Reset so a later setVoiceRecordingState(COUNTDOWN) isn't a same-value no-op.
    setVoiceRecordingState(VoiceRecordingState.IDLE);

    let stream: MediaStream | null = null;
    let connection: VoiceStreamConnection | null = null;

    try {
      // 1. Request microphone (voice) or camera+microphone (video) access
      stream = await requestMediaStream(isVideoMode ? MediaSourceType.CAMERA_MIC : MediaSourceType.MIC);
      audioStreamRef.current = stream;
      setLearnerMediaStream(stream);
      setMicrophoneEnabled(true);

      // Set up AnalyserNode for silence detection (reused across turns)
      initializeAnalyser(stream);
      // Permission acquired; keep mic muted until it's the learner's turn
      setMicActive(false);

      // 2. Start role-play session to get JWT and endpoint
      const sessionRes = await wrapThunkAction(
        dispatch(startRolePlaySession({
          catalogId: currentCatalogId,
          aiRolePlayId,
        })),
      );

      const sessionPayload = sessionRes.payload as StartSessionPayload;
      if (!sessionPayload?.jwtToken || !sessionPayload?.aiApiEndpoint) {
        throw new Error('Invalid session payload');
      }

      const normalizedEndpoint = sessionPayload.aiApiEndpoint.replace(/\/+$/, '');
      const initialSessionData: SessionData = {
        aiApiEndpoint: normalizedEndpoint,
        jwtToken: sessionPayload.jwtToken,
        submissionId: sessionPayload.submissionId || null,
        expiresAt: sessionPayload.expiresAt || null,
        languageLocale: sessionPayload.languageLocale,
      };

      setSessionData(initialSessionData);
      sessionDataRef.current = initialSessionData;
      submissionIdRef.current = sessionPayload.submissionId || null;

      setIsTimeout(false);

      // One session-scoped controller — never replaced during the session so unmount's abort()
      // always propagates. The local `signal` is captured here; the loop reads it directly rather
      // than going through the ref, so ref-nulling in unmount cleanup can't hide the abort.
      const sessionController = new AbortController();
      abortControllerRef.current = sessionController;
      const { signal } = sessionController;

      // Video mode: mint (or re-mint — the endpoint is idempotent) the LiveAvatar
      // session and join its LiveKit room. Called before the first turn, after
      // resume from a stream-error pause, on WS close 4011, and when the LiveKit
      // room drops mid-conversation.
      let lastAvatarConnectionState: AvatarConnectionState | null = null;
      // Guards the drop-triggered auto-recovery so overlapping disconnect events
      // can't stack concurrent re-mints.
      let isAutoRemintingAvatarSession = false;
      const mintAvatarSessionAndConnect = async (): Promise<void> => {
        const avatarToken = await ensureFreshToken();
        const attemptMint = () => wrapThunkAction(dispatch(startAvatarSession({
          aiApiEndpoint: sessionDataRef.current.aiApiEndpoint,
          jwtToken: avatarToken,
        })));

        let avatarPayload: AvatarSessionPayload;
        try {
          avatarPayload = (await attemptMint()).payload as AvatarSessionPayload;
        } catch (rejectedAction) {
          // 409 — another worker holds the session lock; wait out retry_after once.
          const avatarSessionError = (rejectedAction as { payload?: AvatarSessionError })?.payload;
          if (avatarSessionError?.status !== 409 || signal.aborted) {
            throw Object.assign(new Error('Failed to start avatar session'), { status: avatarSessionError?.status });
          }
          const retryAfterSeconds = avatarSessionError.retryAfter ?? AVATAR_SESSION_DEFAULT_RETRY_AFTER_S;
          await new Promise<void>((resolve) => {
            const timer = setTimeout(resolve, retryAfterSeconds * 1000);
            signal.addEventListener('abort', () => { clearTimeout(timer); resolve(); }, { once: true });
          });
          if (signal.aborted) throw new DOMException('Avatar session aborted', 'AbortError');
          avatarPayload = (await attemptMint().catch((retryRejectedAction) => {
            throw Object.assign(
              new Error('Failed to start avatar session'),
              { status: (retryRejectedAction as { payload?: AvatarSessionError })?.payload?.status },
            );
          })).payload as AvatarSessionPayload;
        }

        if (!avatarPayload?.livekitUrl || !avatarPayload?.livekitClientToken) {
          throw new Error('Invalid avatar session payload');
        }
        setAvatarBackgroundImageUrl(avatarPayload.backgroundImageUrl);
        if (signal.aborted) throw new DOMException('Avatar session aborted', 'AbortError');

        if (!avatarPipelineRef.current) {
          const pipeline = createAvatarVideoPipeline();
          pipeline.onConnectionState((state) => {
            setAvatarConnectionState(state);
            const previousState = lastAvatarConnectionState;
            lastAvatarConnectionState = state;
            // The room dropped mid-conversation (not a failed connect attempt, and
            // not our own teardown) — re-mint the avatar session and rejoin.
            if (state === 'disconnected'
              && (previousState === 'connected' || previousState === 'reconnecting')
              && !signal.aborted
              && !isAutoRemintingAvatarSession) {
              isAutoRemintingAvatarSession = true;
              mintAvatarSessionAndConnect().catch((avatarError) => {
                if (!isAbortError(avatarError)) {
                  // eslint-disable-next-line no-console
                  console.warn('Failed to recover the avatar session after a LiveKit disconnect', avatarError);
                }
              }).finally(() => {
                isAutoRemintingAvatarSession = false;
              });
            }
          });
          avatarPipelineRef.current = pipeline;
          if (avatarVideoElRef.current) pipeline.attach(avatarVideoElRef.current);
        }
        await avatarPipelineRef.current.connect(avatarPayload.livekitUrl, avatarPayload.livekitClientToken);
      };

      if (isVideoMode) {
        await mintAvatarSessionAndConnect();
      }

      // 3. Open the voice-stream connection for the opening turn. Later turns reconnect
      // further down the loop, since the server closes the connection after turn_complete.
      const token = await ensureFreshToken();
      connection = await connectVoiceStream({
        aiApiEndpoint: sessionDataRef.current.aiApiEndpoint,
        token,
        abortSignal: signal,
        handlersRef,
      });

      // 4. Run the voice conversation loop (iterative — avoids unbounded call-stack growth)
      // AI persona always speaks first, so the first turn carries no user audio.
      let isFirstTurn = true;

      // Learner's turn — enable the mic (and in video mode the per-turn camera
      // recorder) only while recording, then mute again. Shared by the main loop
      // and the stream/transcript-error resume paths.
      const runLearnerTurn = async (activeConnection: VoiceStreamConnection) => {
        setLearnerVoiceCardState(VoiceCardState.SPEAKING);
        setMicActive(true);
        if (isVideoMode && audioStreamRef.current) {
          startLearnerVideoTurn(audioStreamRef.current);
        }

        try {
          await recordLearnerTurn(signal, activeConnection.sendAudioChunk);
        } catch (recordError) {
          cancelLearnerVideoTurn();
          throw recordError;
        }
        activeConnection.sendAudioEnd();
        isFirstTurn = false;

        setMicActive(false);
        setLearnerVoiceCardState(VoiceCardState.IDLE);

        if (isVideoMode) {
          // Hold the finished recording until turn_complete delivers its turn_id
          // (which arrives with the AI's reply to this very turn).
          pendingLearnerVideoTurnRef.current = await stopLearnerVideoTurn();
        }
      };

      while (!signal.aborted) {
        const isOpeningTurn = isFirstTurn;

        let hasFirstAudioDelta = false;
        let conversationClosingReason: ClosingReason | null = null;
        let hadStreamError = false;
        // True when the error occurred after audio had already started streaming (connection drop
        // or AI service error mid-audio) — triggers retry-tts instead of re-recording.
        let hadAudioStreamingError = false;
        // True when the AI service couldn't transcribe the learner's just-sent audio — recovered
        // by reconnecting on a fresh socket and retrying the turn, not retry-tts or resuming as-is.
        let hadTranscriptError = false;
        // True on an unrecoverable close code (bad JWT, expired session, etc) — reset entirely.
        let isFatalError = false;
        // Video mode only: WS close 4011 — the avatar session vanished server-side.
        // Recovered by re-minting the avatar session and reconnecting, not a reset.
        let hadAvatarSessionMissing = false;

        // Video mode only: resolved by onAvatarSpeechEnded/onAvatarError below, gating
        // this turn's playbackDone on the avatar actually finishing (or degrading).
        let resolveAvatarPlaybackDone: (() => void) | null = null;
        const avatarPlaybackDone = new Promise<void>((resolve) => {
          resolveAvatarPlaybackDone = resolve;
        });

        const {
          appendChunk,
          finishStreaming: finishAudioStreaming,
          discardSentence,
          playbackDone,
          cleanup: cleanupTurnAudio,
        } = isVideoMode
          ? createNoopPlaybackPipeline(avatarPlaybackDone)
          : createAudioPlaybackPipeline(setAiMediaStream, signal);

        setAiVoiceCardState(VoiceCardState.THINKING);
        setLearnerVoiceCardState(VoiceCardState.IDLE);

        let bufferedFirstTurnAudioChunks: Array<{ bytes: Uint8Array; sentenceId: number }> = [];
        let isFirstTurnAudioDoneWhileIntroClosed = false;
        let hasStartedFirstTurnCountdown = false;
        let isFirstTurnIntroOpen = !isOpeningTurn;
        // Named so it can be removed once the countdown completes normally — see
        // openFirstTurnIntro below.
        let onCountdownAbort: (() => void) | null = null;

        const flushBufferedFirstTurnAudio = () => {
          bufferedFirstTurnAudioChunks.forEach(({ bytes, sentenceId }) => appendChunk(bytes, sentenceId));
          bufferedFirstTurnAudioChunks = [];
        };

        // While holding the opening turn's audio for the countdown, don't finish the real
        // pipeline yet (it has no chunks appended, so playbackDone would resolve instantly).
        // Defer to openFirstTurnIntro's flush instead.
        const finishAudioForTurn = () => {
          if (isOpeningTurn && !isFirstTurnIntroOpen) {
            isFirstTurnAudioDoneWhileIntroClosed = true;
            return;
          }
          finishAudioStreaming();
          isAudioDone = true;
          checkTurnSettled();
        };

        const openFirstTurnIntro = () => {
          if (isFirstTurnIntroOpen || signal.aborted) return;

          isFirstTurnIntroOpen = true;
          if (onCountdownAbort) {
            signal.removeEventListener('abort', onCountdownAbort);
            onCountdownAbort = null;
          }
          setVoiceRecordingState(VoiceRecordingState.RECORDING);
          if (isVideoMode) {
            // Release the server-held opening utterance now that the avatar's
            // video element is visible — the server won't dispatch avatar
            // speech before this (with a timeout fallback), so the learner
            // can't miss the opening line behind the countdown overlay.
            connection?.sendAvatarReady();
            avatarPipelineRef.current?.markAvSyncEvent('avatar_ready_sent');
          }
          flushBufferedFirstTurnAudio();

          if (isFirstTurnAudioDoneWhileIntroClosed) {
            finishAudioStreaming();
            isAudioDone = true;
            checkTurnSettled();
          }
        };

        const startFirstTurnCountdown = () => {
          if (!isOpeningTurn || hasStartedFirstTurnCountdown || signal.aborted) return;

          hasStartedFirstTurnCountdown = true;
          setIsRolePlayStarting(false);
          setVoiceRecordingState(VoiceRecordingState.COUNTDOWN);

          const timer = setTimeout(openFirstTurnIntro, COUNTDOWN_DURATION_MS);
          onCountdownAbort = () => clearTimeout(timer);
          signal.addEventListener('abort', onCountdownAbort, { once: true });
        };

        // This turn is "settled" once both the text stream (`done`) and the audio stream
        // (`audio_done`) have arrived for it, or as soon as an error/turn_complete lands —
        // mirrors the old streamAIResponse() call resolving when its SSE response ended.
        let isTextDone = false;
        let isAudioDone = false;
        let resolveTurnSettled: () => void = () => {};
        const turnSettled = new Promise<void>((resolve) => { resolveTurnSettled = resolve; });
        const checkTurnSettled = () => {
          if (isTextDone && isAudioDone) resolveTurnSettled();
        };
        // Without this, an abort mid-turn would leave `await turnSettled` below hanging
        // forever, since none of the socket callbacks fire again once we've closed it.
        // Named so it can be removed once the turn settles normally — `once: true` only
        // auto-removes it on an actual abort, and the signal is reused for the whole session.
        const onAbortDuringTurn = () => resolveTurnSettled();
        signal.addEventListener('abort', onAbortDuringTurn, { once: true });

        handlersRef.current = {
          onChunk: (chunk) => {
            // Ignore chunks that resolve after this session's abort() fired.
            if (signal.aborted) return;
            if (!hasReceivedNarrativeRef.current) {
              setOpeningNarrative((prev) => prev + chunk.content);
              if (chunk.isLastChunk) {
                hasReceivedNarrativeRef.current = true;
                startFirstTurnCountdown();
              }
            }
            if (chunk.isLastChunk) {
              // Set only when the whole role-play is over, not just this turn.
              if (chunk.closingReason) conversationClosingReason = chunk.closingReason;
              isTextDone = true;
              checkTurnSettled();
            }
          },
          onAudioDelta: ({ audio, sentenceId }) => {
            // Same as onChunk — ignore late chunks after abort.
            if (signal.aborted) return;
            // AI is speaking as soon as audio arrives, even while held for the countdown.
            if (!hasFirstAudioDelta) {
              hasFirstAudioDelta = true;
              setAiVoiceCardState(VoiceCardState.SPEAKING);
            }
            const bytes = Uint8Array.from(atob(audio), (c) => c.charCodeAt(0));
            if (isOpeningTurn && !isFirstTurnIntroOpen) {
              bufferedFirstTurnAudioChunks.push({ bytes, sentenceId });
              return;
            }
            appendChunk(bytes, sentenceId);
          },
          onAudioDone: () => {
            if (signal.aborted) return;
            finishAudioForTurn();
          },
          onAudioError: ({ sentenceId }) => {
            // Not fatal — drop this sentence's audio (wherever it's currently buffered) and keep streaming
            if (signal.aborted) return;
            bufferedFirstTurnAudioChunks = bufferedFirstTurnAudioChunks.filter((chunk) => chunk.sentenceId !== sentenceId);
            discardSentence(sentenceId);
          },
          onError: (err) => {
            if (!isAbortError(err)) {
              if (isFatalCloseCode((err as { code?: number })?.code)) {
                // Unrecoverable — dismiss any Resume button already showing for an earlier,
                // less specific error; the fatal checks below will reset the role-play.
                isFatalError = true;
                cleanupTurnAudio();
                resolveTurnSettled();
                restartResolverRef.current?.();
                return;
              }
              hadStreamError = true;
              hadAudioStreamingError = hasFirstAudioDelta;
              cleanupTurnAudio();
              dispatch(addAlertMessage({
                type: AlertMessageType.WARNING,
                header: t.FORM.OOPS(),
                message: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SOMETHING_WENT_WRONG(),
                customColor: danger,
              }));
            }
            resolveTurnSettled();
          },
          onTranscriptError: (err) => {
            if (!isAbortError(err)) {
              hadTranscriptError = true;
              cleanupTurnAudio();
              dispatch(addAlertMessage({
                type: AlertMessageType.WARNING,
                header: t.FORM.OOPS(),
                message: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SOMETHING_WENT_WRONG(),
                customColor: danger,
              }));
            }
            resolveTurnSettled();
          },
          onTurnComplete: (turnId) => {
            // Just this turn ending, not the role-play — audio_done may never arrive now,
            // so finish via the same finishAudioForTurn path (still countdown-safe).
            if (isVideoMode && !signal.aborted) {
              avatarPipelineRef.current?.markAvSyncEvent('turn_complete', { isOpeningTurn, turnId });
              queuePendingVideoTurnUploads(turnId);
            }
            isTextDone = true;
            finishAudioForTurn();
            checkTurnSettled();
            // Backstop: the server always sends turn_complete after any avatar
            // speech has ended (or never started), so this is a safe no-op once
            // onAvatarSpeechEnded/onAvatarError already resolved it — but it's the
            // only signal at all for a turn that dispatched no avatar audio, which
            // would otherwise hang playbackDone forever.
            resolveAvatarPlaybackDone?.();
          },
          // Inworld's server-side turn detection fires too aggressively and cuts the
          // learner mid-sentence. Disabled in favor of the client's own 1s post-speech
          // silence detector (POST_SPEECH_SILENCE_MS).
          // onTranscriptFinal: () => {
          //   if (signal.aborted) return;
          //   notifyTranscriptFinal();
          // },
          ...(isVideoMode && {
            // The LiveKit audio track carries the avatar's speech, so SPEAKING/IDLE
            // come from these events instead of browser-side audio playback. The
            // speech window also bounds the avatar's per-turn video recording.
            onAvatarSpeechStarted: () => {
              if (signal.aborted) return;
              // eslint-disable-next-line no-console
              console.info('[VideoRoleplay] avatar_speech_started', { isOpeningTurn, timestamp: Date.now() });
              // Dense A/V stats sampling around the speech window — the exact
              // interval where a lips-lead-voice offset is user-visible.
              avatarPipelineRef.current?.markAvSyncEvent('avatar_speech_started', { isOpeningTurn });
              setAiVoiceCardState(VoiceCardState.SPEAKING);
              // A fresh stream every turn — after a re-mint/rejoin the remote
              // tracks are replaced, so a held stream would record nothing.
              const avatarStream = avatarPipelineRef.current?.getRecordingStream();
              // eslint-disable-next-line no-console
              console.info('[VideoRoleplay] avatar recording stream', { hasStream: Boolean(avatarStream) });
              if (avatarStream) {
                startAvatarVideoTurn(avatarStream);
              }
            },
            onAvatarSpeechEnded: () => {
              if (signal.aborted) return;
              // eslint-disable-next-line no-console
              console.info('[VideoRoleplay] avatar_speech_ended', { isOpeningTurn, timestamp: Date.now() });
              avatarPipelineRef.current?.markAvSyncEvent('avatar_speech_ended', { isOpeningTurn });
              setAiVoiceCardState(VoiceCardState.IDLE);
              // Held as a promise — the blob finalizes asynchronously and is
              // consumed (with the learner's) when turn_complete lands.
              pendingAvatarVideoTurnRef.current = stopAvatarVideoTurn();
              resolveAvatarPlaybackDone?.();
            },
            // Non-fatal: this turn's avatar rendering degraded to text-only, so
            // no avatar segment should upload for it. avatar_speech_ended will never
            // arrive for this turn, so playbackDone must be released here too or the
            // turn loop hangs forever awaiting it.
            onAvatarError: (avatarError: string) => {
              discardAvatarVideoTurn();
              // eslint-disable-next-line no-console
              console.warn('Avatar error — this turn degraded to text-only:', avatarError);
              resolveAvatarPlaybackDone?.();
            },
            // Registering this keeps close code 4011 out of the generic onError path.
            onAvatarSessionMissing: () => {
              if (signal.aborted) return;
              hadAvatarSessionMissing = true;
              cleanupTurnAudio();
              resolveTurnSettled();
            },
          }),
        };

        if (isOpeningTurn) {
          // No learner audio on the opening turn — signal immediately so the AI speaks first.
          connection.sendAudioEnd();
        }

        await turnSettled; // eslint-disable-line no-await-in-loop
        // Turn settled on its own (no abort) — remove the listener so it doesn't linger on
        // the session-wide signal; a no-op if it already fired and self-removed.
        signal.removeEventListener('abort', onAbortDuringTurn);

        if (signal.aborted) {
          // Only place left to close this turn's AudioContext on abort mid-stream.
          cleanupTurnAudio();
          setAiVoiceCardState(VoiceCardState.IDLE);
          setLearnerVoiceCardState(VoiceCardState.IDLE);
          break;
        }

        if (isFatalError) {
          connection.close();
          dispatch(addAlertMessage({
            type: AlertMessageType.WARNING,
            header: t.FORM.OOPS(),
            message: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SOMETHING_WENT_WRONG(),
            customColor: danger,
          }));
          setAiVoiceCardState(VoiceCardState.IDLE);
          setLearnerVoiceCardState(VoiceCardState.IDLE);
          setMicActive(false);
          resetConversation();
          break;
        }

        if (hadAvatarSessionMissing) {
          setAiVoiceCardState(VoiceCardState.IDLE);
          setLearnerVoiceCardState(VoiceCardState.IDLE);
          setMicActive(false);
          cancelLearnerVideoTurn();
          discardAvatarVideoTurn();

          // The server closed this turn's socket — reconnect after re-minting.
          connection.close();

          // Recoverable — re-mint the avatar session and reconnect the turn socket,
          // then retry the turn: the loop top re-sends audio_end for the opening
          // turn, and later turns re-record the learner.
          await mintAvatarSessionAndConnect(); // eslint-disable-line no-await-in-loop

          const avatarRetryToken = await ensureFreshToken(); // eslint-disable-line no-await-in-loop
          connection = await connectVoiceStream({ // eslint-disable-line no-await-in-loop
            aiApiEndpoint: sessionDataRef.current.aiApiEndpoint,
            token: avatarRetryToken,
            abortSignal: signal,
            handlersRef,
          });

          if (signal.aborted) break;

          if (!isOpeningTurn) {
            await runLearnerTurn(connection); // eslint-disable-line no-await-in-loop
            if (signal.aborted) break;
          }
          // eslint-disable-next-line no-continue
          continue;
        }

        if (hadStreamError) {
          setAiVoiceCardState(VoiceCardState.IDLE);
          setLearnerVoiceCardState(VoiceCardState.IDLE);
          setMicActive(false);
          if (isVideoMode) {
            // The turn will restart or the role-play reset — either way this
            // turn's partial avatar segment must not survive.
            discardAvatarVideoTurn();
          }

          // Close now rather than leaving it open while the Resume button is showing;
          // reconnect only once the user clicks it.
          connection.close();

          if (isOpeningTurn && !isFirstTurnIntroOpen) {
            resetConversation();
            break;
          }

          if (isOpeningTurn && !hadAudioStreamingError) {
            // First turn failed before any audio arrived — reset back to the start screen
            resetConversation();
            break;
          }

          // Surface a Restart button and pause the loop until the user clicks it
          setIsStreamError(true);
          await new Promise<void>((resolve) => { // eslint-disable-line no-await-in-loop
            const onAbort = () => {
              restartResolverRef.current = null;
              resolve();
            };
            restartResolverRef.current = () => {
              signal.removeEventListener('abort', onAbort);
              resolve();
            };
            signal.addEventListener('abort', onAbort, { once: true });
          });
          setIsStreamError(false);

          if (signal.aborted) break;

          if (isFatalError) {
            // Fatal code arrived while Resume was showing (already dismissed above).
            dispatch(addAlertMessage({
              type: AlertMessageType.WARNING,
              header: t.FORM.OOPS(),
              message: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SOMETHING_WENT_WRONG(),
              customColor: danger,
            }));
            resetConversation();
            break;
          }

          if (hadAudioStreamingError) {
            // Audio started but the stream broke mid-delivery — keep retrying via retry-tts
            // (a plain HTTP/SSE endpoint, independent of the voice-stream socket), showing
            // the restart button again on each failure, until audio plays or session ends.
            let retrySucceeded = false;
            while (!signal.aborted && !retrySucceeded) { // eslint-disable-line no-await-in-loop
              const retryToken = await ensureFreshToken(); // eslint-disable-line no-await-in-loop

              if (signal.aborted) break;

              const {
                appendChunk: retryAppendChunk,
                finishStreaming: retryFinishStreaming,
                discardSentence: retryDiscardSentence,
                playbackDone: retryPlaybackDone,
                cleanup: retryCleanup,
              } = createAudioPlaybackPipeline(setAiMediaStream, signal);

              setAiVoiceCardState(VoiceCardState.THINKING);
              setLearnerVoiceCardState(VoiceCardState.IDLE);

              let hasFirstRetryAudioDelta = false;
              let retryHadError = false;

              await retryTtsStream({ // eslint-disable-line no-await-in-loop
                aiApiEndpoint: sessionDataRef.current.aiApiEndpoint,
                token: retryToken,
                onAudioDelta: ({ audio, sentenceId }) => {
                  if (signal.aborted) return;
                  const bytes = Uint8Array.from(atob(audio), (c) => c.charCodeAt(0));
                  retryAppendChunk(bytes, sentenceId);
                  if (!hasFirstRetryAudioDelta) {
                    hasFirstRetryAudioDelta = true;
                    setAiVoiceCardState(VoiceCardState.SPEAKING);
                  }
                },
                onAudioDone: () => {
                  if (signal.aborted) return;
                  retryFinishStreaming();
                },
                onAudioError: ({ sentenceId }) => {
                  if (signal.aborted) return;
                  retryDiscardSentence(sentenceId);
                },
                onError: (err) => {
                  if (!isAbortError(err)) {
                    retryHadError = true;
                    retryCleanup();
                    dispatch(addAlertMessage({
                      type: AlertMessageType.WARNING,
                      header: t.FORM.OOPS(),
                      message: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SOMETHING_WENT_WRONG(),
                      customColor: danger,
                    }));
                  }
                },
                abortSignal: signal,
              }).catch((err) => {
                if (!isAbortError(err)) {
                  retryHadError = true;
                  retryCleanup();
                }
                setAiVoiceCardState(VoiceCardState.IDLE);
                setLearnerVoiceCardState(VoiceCardState.IDLE);
              });

              if (signal.aborted) {
                // Same as the main loop's abort branch — close this retry's AudioContext.
                retryCleanup();
                break;
              }

              // audio_done arrived with no audio_delta — empty TTS response.
              // Treat as a failure so the user sees the restart button rather
              // than silently advancing to their speaking turn with no AI audio.
              if (!retryHadError && !hasFirstRetryAudioDelta) {
                retryHadError = true;
                retryCleanup();
                dispatch(addAlertMessage({
                  type: AlertMessageType.WARNING,
                  header: t.FORM.OOPS(),
                  message: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SOMETHING_WENT_WRONG(),
                  customColor: danger,
                }));
              }

              if (retryHadError) {
                setAiVoiceCardState(VoiceCardState.IDLE);
                setLearnerVoiceCardState(VoiceCardState.IDLE);
                setIsStreamError(true);
                await new Promise<void>((resolve) => { // eslint-disable-line no-await-in-loop
                  const onAbort = () => {
                    restartResolverRef.current = null;
                    resolve();
                  };
                  restartResolverRef.current = () => {
                    signal.removeEventListener('abort', onAbort);
                    resolve();
                  };
                  signal.addEventListener('abort', onAbort, { once: true });
                });
                setIsStreamError(false);
                // eslint-disable-next-line no-continue
                continue;
              }

              await retryPlaybackDone; // eslint-disable-line no-await-in-loop
              retryCleanup();
              setAiVoiceCardState(VoiceCardState.IDLE);
              retrySucceeded = true;
            }

            if (signal.aborted) break;
          }

          if (isVideoMode) {
            // Resume from pause re-mints the avatar session (idempotent) so the
            // LiveKit room is live again before the next turn.
            await mintAvatarSessionAndConnect(); // eslint-disable-line no-await-in-loop
          }

          // Already closed above when the error fired — reconnect now, before the learner's turn.
          const streamErrorRetryToken = await ensureFreshToken(); // eslint-disable-line no-await-in-loop
          connection = await connectVoiceStream({ // eslint-disable-line no-await-in-loop
            aiApiEndpoint: sessionDataRef.current.aiApiEndpoint,
            token: streamErrorRetryToken,
            abortSignal: signal,
            handlersRef,
          });

          if (signal.aborted) break;

          // Resume from the learner's turn so the conversation can continue
          if (isOpeningTurn) {
            setOpeningNarrative('');
          }
          await runLearnerTurn(connection); // eslint-disable-line no-await-in-loop

          if (signal.aborted) break;
          // eslint-disable-next-line no-continue
          continue;
        }

        if (hadTranscriptError) {
          setAiVoiceCardState(VoiceCardState.IDLE);
          setLearnerVoiceCardState(VoiceCardState.IDLE);
          setMicActive(false);
          if (isVideoMode) {
            // The turn is retried from the learner's recording — drop any avatar
            // segment held for the failed attempt.
            discardAvatarVideoTurn();
          }

          // Close now rather than leaving it open while Resume is showing; reconnect
          // only once the user clicks it.
          connection.close();

          // Surface a Restart button and pause the loop until the user clicks it
          setIsStreamError(true);
          await new Promise<void>((resolve) => { // eslint-disable-line no-await-in-loop
            const onAbort = () => {
              restartResolverRef.current = null;
              resolve();
            };
            restartResolverRef.current = () => {
              signal.removeEventListener('abort', onAbort);
              resolve();
            };
            signal.addEventListener('abort', onAbort, { once: true });
          });
          setIsStreamError(false);

          if (signal.aborted) break;

          if (isVideoMode) {
            // Same as the stream-error path — re-mint before resuming the turn loop.
            await mintAvatarSessionAndConnect(); // eslint-disable-line no-await-in-loop
          }

          const retryToken = await ensureFreshToken(); // eslint-disable-line no-await-in-loop
          connection = await connectVoiceStream({ // eslint-disable-line no-await-in-loop
            aiApiEndpoint: sessionDataRef.current.aiApiEndpoint,
            token: retryToken,
            abortSignal: signal,
            handlersRef,
          });

          if (signal.aborted) break;

          if (isOpeningTurn) {
            setOpeningNarrative('');
          }
          await runLearnerTurn(connection); // eslint-disable-line no-await-in-loop

          if (signal.aborted) break;
          // eslint-disable-next-line no-continue
          continue;
        }

        // Always wait for audio playback to finish before updating UI state or
        // moving to the next turn — event: done arrives while audio is still playing.
        if (isVideoMode) {
          // eslint-disable-next-line no-console
          console.info('[VideoRoleplay] awaiting playbackDone (avatar finish)', { isOpeningTurn, timestamp: Date.now() });
        }
        await playbackDone; // eslint-disable-line no-await-in-loop
        if (isVideoMode) {
          // eslint-disable-next-line no-console
          console.info('[VideoRoleplay] playbackDone resolved', { isOpeningTurn, timestamp: Date.now() });
        }
        cleanupTurnAudio();

        setAiVoiceCardState(VoiceCardState.IDLE);

        // The opening narrative caption is only needed while the AI speaks its first
        // line — hide it once that line has finished playing.
        if (isOpeningTurn) {
          setOpeningNarrative('');
        }

        if (signal.aborted) break;

        // Either the FE's own turn-based counter (learner recorded silence too many times)
        // or the AI service's own inactivity detection (closing_reason on the done event)
        if (inactivityCountRef.current >= MAX_INACTIVITY_COUNT
            || conversationClosingReason === ClosingReason.INACTIVITY) {
          dispatch(addAlertMessage({
            type: AlertMessageType.WARNING,
            header: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SESSION_ENDED(),
            message: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SESSION_ENDED_DUE_TO_INACTIVITY(),
            customColor: danger,
          }));
          setVoiceRecordingState(VoiceRecordingState.IDLE);
          setAiRolePlayStatus(AIRolePlayStatus.FAILED);
          connection.close();
          break;
        }

        // Conversation ended — update all state only after audio has finished playing.
        if (conversationClosingReason) {
          if (submissionIdRef.current) {
            dispatch(setAiRolePlaySubmissionClosingReason({
              aiRolePlaySubmissionId: submissionIdRef.current,
              closingReason: conversationClosingReason,
              aiRolePlayId,
            }));
          }
          if (conversationClosingReason === ClosingReason.TERMINATED) {
            dispatch(addAlertMessage({
              type: AlertMessageType.WARNING,
              header: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SESSION_ENDED(),
              message: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.ROLE_PLAY_ENDED_DUE_TO_RESPONSE(),
              customColor: danger,
            }));
          }
          setVoiceRecordingState(VoiceRecordingState.COMPLETED);
          const completionTimer = setTimeout(() => {
            setVoiceRecordingState(VoiceRecordingState.IDLE);
            setAiRolePlayStatus(AIRolePlayStatus.COMPLETED);
          }, 2000);
          signal.addEventListener('abort', () => clearTimeout(completionTimer), { once: true });
          connection.close();
          break;
        }

        // Server closes the connection after every turn — reconnect before the learner speaks.
        connection.close();
        const nextTurnToken = await ensureFreshToken(); // eslint-disable-line no-await-in-loop
        connection = await connectVoiceStream({ // eslint-disable-line no-await-in-loop
          aiApiEndpoint: sessionDataRef.current.aiApiEndpoint,
          token: nextTurnToken,
          abortSignal: signal,
          handlersRef,
        });

        if (signal.aborted) break;

        await runLearnerTurn(connection); // eslint-disable-line no-await-in-loop

        if (signal.aborted) break;
      }
    } catch (error) {
      if ((error as Error)?.name === 'NotAllowedError' || (error as Error)?.name === 'PermissionDeniedError') {
        setMicrophoneEnabled(false);

        dispatch(openConfirmationDialog({
          title: isVideoMode
            ? t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.CAMERA_PERMISSION_DENIED.TITLE()
            : t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.MICROPHONE_DISABLED_OVERLAY.TITLE(),
          bodyText: isVideoMode
            ? t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.CAMERA_PERMISSION_DENIED.DESCRIPTION()
            : t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.MICROPHONE_DISABLED_OVERLAY.DESCRIPTION(),
          confirmText: t.FORM.TRY_AGAIN(),
          cancelText: t.FORM.BACK(),
          onCancel: resetConversation,
          onConfirm: handleStartVoiceConversationRef.current,
        }));
      } else if (!isAbortError(error)) {
        dispatch(addAlertMessage({
          type: AlertMessageType.WARNING,
          header: t.FORM.OOPS(),
          message: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SOMETHING_WENT_WRONG(),
          customColor: danger,
        }));
      }
      setAiVoiceCardState(VoiceCardState.IDLE);
      setLearnerVoiceCardState(VoiceCardState.IDLE);
      setVoiceRecordingState(VoiceRecordingState.IDLE);
      // Skip state resets on abort — either resetConversation already set them
      // (exit flow) or a new session is already running (Start Over) and its own
      // state must not be overwritten.
      if (!isAbortError(error)) {
        setAiRolePlayStatus(AIRolePlayStatus.NOT_STARTED);
        setShowScenarioDetails(true);
      }
    } finally {
      // Always release all audio/connection resources regardless of how the session ended.
      // setMicActive(false) must be here — if recordLearnerTurn throws (abort or
      // null-stream), control jumps directly to catch/finally and skips the
      // inline setMicActive(false) calls after each await.
      setMicActive(false);
      setIsStreamError(false);
      restartResolverRef.current = null;
      cleanupRecorder();
      if (isVideoMode) {
        cancelLearnerVideoTurn();
        pendingLearnerVideoTurnRef.current = null;
        discardAvatarVideoTurn();
      }
      connection?.close();
      // Stop our own stream without touching the ref or shared UI state if a
      // newer session has already taken over. The session ID check is immune to
      // the audioStreamRef timing gap (new session sets it only after getUserMedia
      // resolves, which is slower than the microtask that runs this finally block).
      const isCurrentSession = sessionIdRef.current === thisSessionId;
      if (stream) {
        stopMediaStreams(stream);
        if (isCurrentSession) {
          audioStreamRef.current = null;
        }
      }
      if (isCurrentSession) {
        setAiMediaStream(null);
        setLearnerMediaStream(null);
        setIsRolePlayStarting(false);
        if (isVideoMode) {
          // Tear down the LiveKit room and, best effort, the server-side avatar
          // session — covers conversation end (completed/terminated/failed) and
          // modal close alike. Start Over skips this (isCurrentSession is false)
          // and re-mints on top of the still-idempotent server session instead.
          avatarPipelineRef.current?.cleanup();
          avatarPipelineRef.current = null;
          setAvatarConnectionState(null);
          const { aiApiEndpoint, jwtToken } = sessionDataRef.current;
          if (aiApiEndpoint && jwtToken) {
            dispatch(stopAvatarSession({ aiApiEndpoint, jwtToken }));
          }
        }
      }
    }
  }, [
    dispatch,
    currentCatalogId,
    aiRolePlayId,
    setAiRolePlayStatus,
    setShowScenarioDetails,
    setSessionData,
    setIsTimeout,
    setIsRolePlayStarting,
    setMicrophoneEnabled,
    audioStreamRef,
    abortControllerRef,
    ensureFreshToken,
    recordLearnerTurn,
    setMicActive,
    initializeAnalyser,
    cleanupRecorder,
    notifyTranscriptFinal,
    setOpeningNarrative,
    setVoiceRecordingState,
    resetConversation,
    setInactivityCount,
    isVideoMode,
    startLearnerVideoTurn,
    stopLearnerVideoTurn,
    cancelLearnerVideoTurn,
    startAvatarVideoTurn,
    stopAvatarVideoTurn,
    discardAvatarVideoTurn,
    queuePendingVideoTurnUploads,
  ]);

  handleStartVoiceConversationRef.current = handleStartVoiceConversation;

  const handleRestartFromStreamError = useCallback(() => {
    if (restartResolverRef.current) {
      restartResolverRef.current();
      restartResolverRef.current = null;
    }
  }, []);

  return {
    handleStartVoiceConversation,
    aiVoiceCardState,
    learnerVoiceCardState,
    aiMediaStream,
    learnerMediaStream,
    isStreamError,
    handleRestartFromStreamError,
    attachAvatarVideo,
    avatarConnectionState,
    avatarBackgroundImageUrl,
  };
};

export default useVoiceAiConversationHandlers;
